import React, { useState, useEffect } from 'react';
import './App.css';
import ProjectTable from './components/ProjectTable';
import TaskTable from './components/TaskTable';
import ProjectModal from './components/ProjectModal';
import TaskModal from './components/TaskModal';
import AuthModal from './components/AuthModal';
import GanttChart from './components/GanttChart';

const API_URL = '/api';

function App() {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState({});
  const [loading, setLoading] = useState(true);
  
  const [selectedProjectIds, setSelectedProjectIds] = useState(new Set());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/projects`);
      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error("获取项目失败:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasksForProject = async (projectId) => {
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/tasks`);
      const data = await response.json();
      setTasks(prevTasks => ({
        ...prevTasks,
        [projectId]: data
      }));
    } catch (error) {
      console.error(`获取项目 ${projectId} 的任务失败:`, error);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    const promises = Array.from(selectedProjectIds).map(id => fetchTasksForProject(id));
    Promise.all(promises);

    // Cleanup tasks for deselected projects
    setTasks(prevTasks => {
        const keptTasks = {};
        for(const id of selectedProjectIds) {
            if(prevTasks[id]) {
                keptTasks[id] = prevTasks[id];
            }
        }
        return keptTasks;
    })

  }, [selectedProjectIds]);

  const handleProjectSelect = (project) => {
    const projectId = project.id.toString();
    const newSelection = new Set(selectedProjectIds);
    if (newSelection.has(projectId)) {
      newSelection.delete(projectId);
    } else {
      newSelection.add(projectId);
    }
    setSelectedProjectIds(newSelection);
  };

  const handleToggleProjectSelection = (projectId) => {
    const newSelection = new Set(selectedProjectIds);
    if (newSelection.has(projectId)) {
      newSelection.delete(projectId);
    } else {
      newSelection.add(projectId);
    }
    setSelectedProjectIds(newSelection);
  };

  const handleToggleSelectAll = () => {
    if (selectedProjectIds.size === projects.length) {
      setSelectedProjectIds(new Set());
    } else {
      const allProjectIds = new Set(projects.map(p => p.id.toString()));
      setSelectedProjectIds(allProjectIds);
    }
  };

  const handleExport = async () => {
    let projectIdsToExport = Array.from(selectedProjectIds);

    if (projectIdsToExport.length === 0) {
      alert('请选择一个或多个项目再导出！');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectIds: projectIdsToExport }),
      });
      if (!response.ok) throw new Error('导出失败');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'export.xlsx';
      if (contentDisposition) {
        const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''([\w%\-.]+)(?:;|$)/i);
        if (utf8FilenameMatch && utf8FilenameMatch[1]) {
            filename = decodeURIComponent(utf8FilenameMatch[1]);
        } else {
            const asciiFilenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
            if (asciiFilenameMatch && asciiFilenameMatch[1]) {
                filename = asciiFilenameMatch[1];
            }
        }
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("导出错误:", error);
        alert('导出文件时出错！');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/import`, {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        alert('导入成功！');
        fetchProjects();
        setTasks({});
        setSelectedProjectIds(new Set());
      } else {
        alert('导入失败！');
      }
    } catch (error) {
      console.error('导入错误:', error);
      alert('导入出错！');
    }
  };

  const handleSaveProject = async (projectData) => {
    const isEditing = !!editingProject;
    const url = isEditing ? `${API_URL}/projects/${editingProject.id}` : `${API_URL}/projects`;
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData),
      });
      if (response.ok) {
        fetchProjects();
        setEditingProject(null);
        setIsModalOpen(false);
      } else {
        alert('保存项目失败!');
      }
    } catch (error) {
      console.error('保存项目错误:', error);
      alert('保存项目出错!');
    }
  };

  const handleSaveTask = async (taskData) => {
    const isEditing = !!editingTask?.id;
    const url = isEditing ? `${API_URL}/tasks/${editingTask.id}` : `${API_URL}/tasks`;
    const method = isEditing ? 'PUT' : 'POST';

    const projectIdForTask = isEditing ? editingTask.projectId : currentProjectId;

    const dataToSave = isEditing 
        ? taskData 
        : { ...taskData, projectId: projectIdForTask, parentId: editingTask?.parentId || null };

    try {
      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSave),
      });
      if (response.ok) {
        fetchTasksForProject(projectIdForTask);
        fetchProjects();
        closeTaskModal();
      } else {
        alert('保存任务失败!');
      }
    } catch (error) {
      console.error('保存任务错误:', error);
      alert('保存任务出错!');
    }
  };

  const handleAddProjectClick = () => {
    setEditingProject(null);
    setIsModalOpen(true);
  };

  const handleEditProjectClick = (project) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('确定要删除这个项目吗？其下的所有任务也将被删除。')) {
      try {
        const response = await fetch(`${API_URL}/projects/${projectId}`, { method: 'DELETE' });
        if (response.ok) {
          fetchProjects(); 
          const newSelection = new Set(selectedProjectIds);
          newSelection.delete(projectId.toString());
          setSelectedProjectIds(newSelection);
          // Also remove tasks from state
          setTasks(prev => {
            const newTasks = { ...prev };
            delete newTasks[projectId.toString()];
            return newTasks;
          });
        } else {
          alert('删除项目失败!');
        }
      } catch (error) {
        console.error('删除项目错误:', error);
        alert('删除项目出错!');
      }
    }
  };

  const handleUpdateTaskStatus = async (taskId, projectId) => {
    const taskToUpdate = tasks[projectId]?.find(t => t.id === taskId);
    if (!taskToUpdate) return;

    let updatedProperties = {};
    const now = new Date().toISOString();

    if (taskToUpdate.status === '未开始') {
      updatedProperties = { status: '进行中', startDate: now };
    } else if (taskToUpdate.status === '进行中') {
      updatedProperties = { status: '已完成', endDate: now };
    } else {
      return; // Do nothing if task is already completed or in another state
    }
    
    try {
      const response = await fetch(`${API_URL}/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedProperties),
      });

      if (response.ok) {
        const { updatedTasks } = await response.json();
        
        setTasks(prevTasks => {
          const newTasksForProject = [...prevTasks[projectId]];
          const updatedTasksMap = new Map(updatedTasks.map(t => [t.id, t]));

          const finalTasks = newTasksForProject.map(t => updatedTasksMap.get(t.id) || t);

          // Force a sort by creation date to ensure stable order after updates.
          finalTasks.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

          return {
            ...prevTasks,
            [projectId]: finalTasks,
          };
        });

        fetchProjects(); // Refresh projects to update progress
      } else {
        alert('更新任务状态失败!');
      }
    } catch (error) {
      console.error('更新任务状态错误:', error);
      alert('更新任务状态出错!');
    }
  };

  const handleDeleteTask = async (taskId, projectId) => {
    if (window.confirm('确定要删除这个任务吗？')) {
      try {
        const response = await fetch(`${API_URL}/tasks/${taskId}`, { method: 'DELETE' });
        if (response.ok) {
          fetchTasksForProject(projectId);
          fetchProjects();
        } else {
          alert('删除任务失败!');
        }
      } catch (error) {
        console.error('删除任务错误:', error);
        alert('删除任务出错!');
      }
    }
  };

  const handleAddTaskClick = (projectId, parentId = null) => {
    setCurrentProjectId(projectId);
    setEditingTask({ parentId });
    setIsTaskModalOpen(true);
  };

  const handleEditTaskClick = (task) => {
    setEditingTask(task);
    setIsTaskModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
  };

  const closeTaskModal = () => {
    setIsTaskModalOpen(false);
    setEditingTask(null);
    setCurrentProjectId(null);
  };

  const handleAuth = (password) => {
    if (password === '123123') {
      setIsAuthenticated(true);
      setIsAuthModalOpen(false);
    } else {
      alert('密码错误');
    }
  };

  const handleAbortBidding = async (projectId) => {
    if (window.confirm('您确定这个项目的标书不适合投标吗？这将删除大部分未完成的任务。')) {
      try {
        const response = await fetch(`${API_URL}/projects/${projectId}/abort-bidding`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          alert('操作成功，相关任务已清理。');
          // Refresh both projects (to get new bid_status) and tasks
          fetchProjects();
          fetchTasksForProject(projectId);
        } else {
          const errorData = await response.json();
          alert(`操作失败: ${errorData.error}`);
        }
      } catch (error) {
        console.error('中止投标流程错误:', error);
        alert('中止投标流程出错!');
      }
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>中鸣数据项目管理系统</h1>
        <button className="btn auth-btn" onClick={() => setIsAuthModalOpen(true)}>删改权限</button>
      </header>
      <main className="container">
        <div className="projects-section">
          <div className="toolbar">
            <h2>中鸣数据投标项目跟踪表</h2>
            <div className="buttons-group">
                <button className="btn" onClick={handleAddProjectClick}>添加项目</button>
                <button className="btn" onClick={handleExport} disabled={selectedProjectIds.size === 0}>导出项目</button>
                <input type="file" id="import-file" style={{ display: 'none' }} onChange={handleFileChange} accept=".xlsx, .xls"/>
                <button className="btn" onClick={() => document.getElementById('import-file').click()}>从 Excel 导入</button>
            </div>
          </div>
          {loading ? <p>正在加载项目...</p> : 
            <ProjectTable 
              projects={projects} 
              onProjectSelect={handleProjectSelect}
              selectedProjectIds={selectedProjectIds}
              onToggleSelection={handleToggleProjectSelection}
              onToggleSelectAll={handleToggleSelectAll}
              onEdit={handleEditProjectClick}
              onDelete={handleDeleteProject}
              isAuthenticated={isAuthenticated}
            />
          }
        </div>
        
        {Array.from(selectedProjectIds).map(projectId => {
            const project = projects.find(p => p.id.toString() === projectId);
            return (
                <div key={projectId} className="tasks-section">
          <div className="toolbar">
                        <h2>任务分解表 {project ? `(${project.name})` : ''}</h2>
                    <div className="buttons-group">
                            <button className="btn" onClick={() => handleAddTaskClick(projectId, null)}>添加任务</button>
                    </div>
            </div>
          <TaskTable
            project={project}
            tasks={tasks[project.id]}
            onEdit={handleEditTaskClick}
            onDelete={(taskId) => handleDeleteTask(taskId, project.id)}
            onAddSubtask={(parentId) => handleAddTaskClick(project.id, parentId)}
            onUpdateStatus={(taskId) => handleUpdateTaskStatus(taskId, project.id)}
            onAbortBidding={() => handleAbortBidding(project.id)}
            isAuthenticated={isAuthenticated}
          />
          <GanttChart key={project.id} project={project} tasks={tasks[project.id] || []} />
        </div>
            );
        })}

      </main>
      <ProjectModal 
        show={isModalOpen}
        onClose={closeModal}
        onSave={handleSaveProject}
        project={editingProject}
      />
      <TaskModal
        show={isTaskModalOpen}
        onClose={closeTaskModal}
        onSave={handleSaveTask}
        task={editingTask}
        existingTasks={currentProjectId ? tasks[currentProjectId] : []}
      />
      <AuthModal
        show={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onAuth={handleAuth}
      />
    </div>
  );
}

export default App;
